import { flip, offset, shift, useFloating } from "@floating-ui/react";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faListDropdown } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/sharp-light-svg-icons";
import { Menu } from "@headlessui/react";
import {
    type ColumnDef,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { CompactTableUI } from "../components/DataTable";
import { SecondaryButton } from "./Buttons";

export interface BulkActionsProps<TFilters = Record<string, unknown>> {
    filters: TFilters;
    selectedIds: string[];
    allSelected: boolean;
    onFinalizeAction: () => void;
}

export interface BulkActionsConfirmationProps<
    TFilters = Record<string, unknown>,
> extends BulkActionsProps<TFilters> {
    visible: boolean;
    onClose: () => void;
}

// A human-readable row for the filter summary shown in the bulk scope picker.
export type FilterSummaryItem = { label: string; value: string };

// Every bulk endpoint answers with this shape. The generated client declares
// one interface per endpoint (BulkEventsResponse, BulkEventsArchiveResponse,
// BulkUpdateUnmatchedDataPoints, …), all structurally identical to this one and
// all typing failures as bare dicts.
export interface BulkActionResult {
    ok: number;
    failed: number;
    failures: Record<string, unknown>[];
    failuresTruncated: boolean;
}

// A failure entry, once narrowed from the bare dict the generated client hands
// us. Normalizing here is what keeps `as BulkFailure[]` out of the call sites.
export interface BulkFailure {
    id: string;
    errors: Record<string, unknown>;
}

const toBulkFailures = (
    failures: BulkActionResult["failures"],
): BulkFailure[] =>
    failures.map((failure) => ({
        id: String(failure.id ?? ""),
        errors: (failure.errors ?? {}) as Record<string, unknown>,
    }));

// Builds the link back to the record a failure refers to. Used both by the
// failures table and by the downloaded CSV, so each feature decides where its
// records live.
export type BulkFailureLinkBuilder = (id: string) => string;

// Map serializer field names to friendlier labels shown in UI/CSV.
const FIELD_LABELS: Record<string, string> = {
    event_status: "Status change",
    responsible: "Assignee",
    main_event: "Linked event",
    form_data: "Form data",
    non_field_errors: "",
};

const labelForField = (key: string): string =>
    key in FIELD_LABELS ? FIELD_LABELS[key] : key;

// Flattens a DRF serializer .errors dict into a single human-readable line.
// Handles nested dicts and lists. Example:
//   {event_status: ["Not allowed"], root_cause: {code: ["Required"]}}
//   -> "Status change: Not allowed | root_cause.code: Required"
const formatErrors = (errors: unknown, prefix = ""): string => {
    if (errors == null) return "";
    if (typeof errors === "string") {
        return prefix ? `${prefix}: ${errors}` : errors;
    }
    if (Array.isArray(errors)) {
        return errors
            .map((e) => formatErrors(e, prefix))
            .filter(Boolean)
            .join("; ");
    }
    if (typeof errors === "object") {
        return Object.entries(errors as Record<string, unknown>)
            .map(([key, value]) => {
                const label = prefix ? `${prefix}.${key}` : labelForField(key);
                return formatErrors(value, label);
            })
            .filter(Boolean)
            .join(" | ");
    }
    const str = String(errors);
    return prefix ? `${prefix}: ${str}` : str;
};

const escapeCsvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

const downloadFailuresCsv = (
    failures: BulkFailure[],
    filenamePrefix: string,
    linkForId: BulkFailureLinkBuilder,
) => {
    const header = "Record link,Error reason";
    const rows = failures.map((f) => {
        // Absolute, so the link still works pasted into a spreadsheet.
        const link = new URL(linkForId(f.id), window.location.origin).href;
        const reason = formatErrors(f.errors) || "Unknown error";
        return `${escapeCsvCell(link)},${escapeCsvCell(reason)}`;
    });
    const csv = [header, ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    a.download = `${filenamePrefix}-failures-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const BulkFailuresTruncatedNote = ({
    result,
}: {
    result: BulkActionResult;
}) => {
    if (!result.failuresTruncated || !result.failures.length) return null;
    return (
        <p className="text-xs text-gray-500 mb-1">
            Only the first {result.failures.length} failures are included in the
            CSV.
        </p>
    );
};

const failureColumns = (
    linkForId: BulkFailureLinkBuilder,
): ColumnDef<BulkFailure, any>[] => [
    {
        accessorKey: "id",
        header: "ID",
        cell: ({ getValue }) => {
            const id = getValue<string>();
            return (
                <a
                    href={linkForId(id)}
                    target="_blank"
                    rel="noopener"
                    className="text-blue-600 hover:underline font-mono text-xs"
                >
                    {id}
                </a>
            );
        },
    },
    {
        accessorKey: "errors",
        header: "Errors",
        cell: ({ getValue }) => {
            const errors = getValue<Record<string, unknown>>();
            return (
                <ul className="text-xs space-y-0.5">
                    {Object.entries(errors).map(([field, messages]) => {
                        const items = Array.isArray(messages)
                            ? messages
                            : [messages];
                        return items.map((msg, i) => (
                            <li key={`${field}-${i}`}>
                                <span className="font-semibold capitalize">
                                    {field.replaceAll("_", " ")}
                                </span>
                                {": "}
                                {String(msg)}
                            </li>
                        ));
                    })}
                </ul>
            );
        },
    },
];

export const BulkFailuresPreview = ({
    result,
    linkForId,
}: {
    result: BulkActionResult;
    linkForId: BulkFailureLinkBuilder;
}) => {
    const columns = useMemo(() => failureColumns(linkForId), [linkForId]);
    const failures = useMemo(
        () => toBulkFailures(result.failures),
        [result.failures],
    );
    const table = useReactTable({
        data: failures,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        autoResetPageIndex: false,
    });

    return (
        <div className="h-44 border rounded overflow-hidden">
            <BulkFailuresTruncatedNote result={result} />
            <CompactTableUI
                table={table}
                dataName="failures"
                columns={columns}
                loadingData={false}
                itemCount={failures.length}
            />
        </div>
    );
};

export const BulkFailuresDownloadButton = ({
    result,
    filenamePrefix,
    linkForId,
}: {
    result: BulkActionResult;
    filenamePrefix: string;
    linkForId: BulkFailureLinkBuilder;
}) => {
    if (result.failures.length === 0) return null;
    return (
        <SecondaryButton
            type="button"
            onClick={() =>
                downloadFailuresCsv(
                    toBulkFailures(result.failures),
                    filenamePrefix,
                    linkForId,
                )
            }
        >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            Download failures as CSV
        </SecondaryButton>
    );
};

const formatEventStatus = (status: string) =>
    status
        .replaceAll("_", " ")
        .toLowerCase()
        .replace(/\b\w/g, (character) => character.toUpperCase());

const formatFilterSummaryValue = ({ label, value }: FilterSummaryItem) =>
    label === "Status"
        ? value.split(",").map(formatEventStatus).join(", ")
        : value;

type BulkScopeSelectorProps = {
    allSelected: boolean;
    allSelectedCount?: number;
    filterSummary?: FilterSummaryItem[];
    noun?: string;
    onChange: (allSelected: boolean) => void;
    selectedCount: number;
};

export const BulkScopeSelector = ({
    allSelected,
    allSelectedCount,
    filterSummary = [],
    noun = "rows",
    onChange,
    selectedCount,
}: BulkScopeSelectorProps) => {
    return (
        <div className="border-y border-gray-200">
            <label className="flex cursor-pointer gap-3 pt-3 pb-3">
                <input
                    type="radio"
                    name="bulk-action-scope"
                    checked={allSelected}
                    onChange={() => onChange(true)}
                    className="mt-1 h-5 w-5 accent-blue-600"
                />
                <span>
                    <span className="block text-sm font-[1000] [-webkit-text-stroke:0.2px_currentColor] text-gray-900">
                        All filtered {noun}{" "}
                        <span className="font-normal text-gray-600">
                            — {allSelectedCount ?? "…"} rows
                        </span>
                    </span>
                    <span className="mt-1 block text-sm text-gray-500">
                        {filterSummary.length === 0
                            ? "No filters applied"
                            : `Everything matching these ${filterSummary.length} filter${filterSummary.length !== 1 ? "s" : ""}:`}
                    </span>
                    {filterSummary.length > 0 && (
                        <span className="mt-2 flex flex-wrap gap-2">
                            {filterSummary.map(({ label, value }) => (
                                <span
                                    key={`${label}-${value}`}
                                    className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs text-gray-600"
                                >
                                    {label}{" "}
                                    <strong className="font-[1000] [-webkit-text-stroke:0.2px_currentColor] text-ae-blue-900">
                                        {formatFilterSummaryValue({
                                            label,
                                            value,
                                        })}
                                    </strong>
                                </span>
                            ))}
                        </span>
                    )}
                </span>
            </label>
            <label className="flex cursor-pointer gap-3 border-t border-gray-200 pt-3 pb-3">
                <input
                    type="radio"
                    name="bulk-action-scope"
                    checked={!allSelected}
                    onChange={() => onChange(false)}
                    className="mt-1 h-5 w-5 accent-blue-600"
                />
                <span>
                    <span className="block text-sm font-[1000] [-webkit-text-stroke:0.2px_currentColor] text-gray-600">
                        My selection{" "}
                        <span className="font-normal">
                            — {selectedCount} rows
                        </span>
                    </span>
                    <span className="mt-1 block text-sm text-gray-500">
                        Only the {selectedCount} rows you ticked.
                    </span>
                </span>
            </label>
        </div>
    );
};

export const BulkMenuItem = ({
    icon,
    label,
    enabled,
    disabledTitle,
    onClick,
}: {
    icon: IconDefinition;
    label: string;
    enabled: boolean;
    disabledTitle: string;
    onClick: () => void;
}) => (
    <Menu.Item
        as="button"
        disabled={!enabled}
        onClick={onClick}
        title={enabled ? undefined : disabledTitle}
        className={`rounded p-1 flex items-center gap-1 text-left ${
            enabled
                ? "hover:bg-ae-slate-200"
                : "text-gray-400 cursor-not-allowed"
        }`}
    >
        <FontAwesomeIcon icon={icon} />
        {label}
    </Menu.Item>
);

export type BulkActionsMenuVariant = "primary" | "footer";

// "Bulk Actions" dropdown trigger + panel. Children should be BulkMenuItems.
export const BulkActionsMenu = ({
    disabled,
    variant = "primary",
    children,
}: {
    disabled: boolean;
    variant?: BulkActionsMenuVariant;
    children: React.ReactNode;
}) => {
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [offset(2), flip(), shift({ padding: 4 })],
    });

    return (
        <Menu as="div" className="relative">
            <Menu.Button
                ref={refs.setReference}
                disabled={disabled}
                title={
                    disabled
                        ? "Select rows on the table to enable bulk actions"
                        : undefined
                }
                className={
                    variant === "primary"
                        ? `
                h-7 rounded flex items-center justify-between px-4 border gap-2
                ${disabled ? "bg-neutral-300" : "bg-ae-blue-550"} text-white
            `
                        : `py-0.5 px-1 rounded flex items-center gap-1 ${
                              disabled
                                  ? "text-neutral-300 cursor-not-allowed"
                                  : "text-ae-blue-500 hover:bg-ae-blue-100 hover:text-ae-blue-600"
                          }`
                }
            >
                {variant === "primary" ? (
                    <>
                        <FontAwesomeIcon icon={faListDropdown} />
                        Bulk Actions
                    </>
                ) : (
                    <>
                        Bulk Actions
                        <FontAwesomeIcon icon={faListDropdown} />
                    </>
                )}
            </Menu.Button>
            <Menu.Items
                ref={refs.setFloating}
                style={floatingStyles}
                className="bg-white shadow-lg rounded border-2 p-1 flex flex-col gap-1 whitespace-nowrap z-50"
            >
                {children}
            </Menu.Items>
        </Menu>
    );
};
